import React from 'react';
import { 
  Users, 
  Video, 
  Wrench, 
  Heart, 
  Building2,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface TracksSectionProps {
  registrationUrl: string;
  isPastEvent?: boolean;
  isTentative?: boolean;
}

const TracksSection: React.FC<TracksSectionProps> = ({ 
  registrationUrl, 
  isPastEvent = false,
  isTentative = false 
}) => {
  const tracks = [
    {
      id: 'attend-virtual',
      icon: Video,
      title: 'Attend Virtually',
      subtitle: 'Watch the livestream',
      description: 'Join from anywhere via YouTube & Twitch livestream.',
      benefits: ['Global access', 'Live chat', 'Free to join'],
      cta: 'Register for Livestream',
      variant: 'outline' as const,
      href: registrationUrl,
    },
    {
      id: 'attend-person',
      icon: Users,
      title: 'Attend In-Person',
      subtitle: 'Join us at the venue',
      description: 'Full event experience with networking and meals.',
      benefits: ['Free to attend', 'Networking', 'Meals included'],
      cta: 'Register to Attend',
      variant: 'default' as const,
      featured: true,
      href: registrationUrl,
    },
    {
      id: 'participate',
      icon: Wrench,
      title: 'Participate',
      subtitle: 'Join a build team',
      description: 'Build AI solutions with your team. Remote option available.',
      benefits: ['Hands-on building', 'Mentorship access', 'Remote or in-person'],
      cta: 'Join a Team',
      variant: 'default' as const,
      featured: true,
      href: registrationUrl,
    },
  ];

  const sponsorPartnerTracks = [
    {
      id: 'sponsor',
      icon: Building2,
      title: 'Sponsor',
      description: 'Support the event and connect with top talent.',
      benefits: ['Brand visibility', 'Talent access', 'Impact story'],
      cta: 'Become a Sponsor',
      href: '/sponsors',
    },
    {
      id: 'partner',
      icon: Heart,
      title: 'Partner (Nonprofit)',
      description: 'Submit your challenge for teams to solve.',
      benefits: ['Free AI prototype', 'Dedicated team', 'Event showcase'],
      cta: 'Apply as Partner',
      href: '/nonprofits',
    },
  ];

  return (
    <section className="space-y-6 print:break-inside-avoid print:break-before-page">
      <div>
        <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
          Ways to Get Involved
        </p>
        <h2 className="text-2xl md:text-3xl font-bold font-display">
          Find Your Track
        </h2>
      </div>

      {/* Main Participation Tracks */}
      <div className="grid md:grid-cols-3 gap-4">
        {tracks.map((track) => {
          const IconComponent = track.icon;
          return (
            <Card 
              key={track.id} 
              className={`relative overflow-hidden transition-all hover:border-primary/50 ${
                track.featured ? 'border-primary/30 bg-primary/5' : 'bg-card border-border'
              }`}
            >
              {track.featured && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg text-xs">Popular</Badge>
                </div>
              )}
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    track.featured ? 'bg-primary/20' : 'bg-muted/50'
                  }`}>
                    <IconComponent className={`h-5 w-5 ${track.featured ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-display">{track.title}</h3>
                    <p className="text-xs text-muted-foreground">{track.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {track.description}
                </p>

                <div className="flex flex-wrap gap-1.5">
                  {track.benefits.map((benefit) => (
                    <span key={benefit} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-secondary shrink-0" />
                      {benefit}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sponsor & Partner Tracks */}
      <div className="grid md:grid-cols-2 gap-4">
        {sponsorPartnerTracks.map((track) => {
          const IconComponent = track.icon;
          return (
            <Card key={track.id} className="bg-muted/20 border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                  <IconComponent className="h-5 w-5 text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold font-display text-sm">{track.title}</h3>
                  <p className="text-xs text-muted-foreground truncate">{track.description}</p>
                </div>
                <Button variant="outline" size="sm" asChild className="shrink-0">
                  <a href={track.href}>
                    {track.cta}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Primary CTA - hidden on print (QR code in footer handles this) */}
      {!isPastEvent && !isTentative && (
        <div className="text-center pt-2 print:hidden">
          <Button size="lg" asChild>
            <a href={registrationUrl}>
              Register Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      )}
    </section>
  );
};

export default TracksSection;